import React from 'react';
import styled from 'styled-components';
import * as Amplitude from 'utils/amplitude';
import Lottie from 'react-lottie-player';

import BALLON_ANIMATION from 'assets/animations/balloon.json';
import Headline1 from '@twicapp/ui/components/Headline1';
import Subtitle1 from '@twicapp/ui/components/Subtitle1';
import Button from '@twicapp/ui/components/Button';
import Divider from '@twicapp/ui/components/legacy/Divider';
import SvgIcon from '@twicapp/ui/components/SvgIcon';
import { ReactComponent as LinkSvg } from '@twicapp/ui/icons/arrow-top-right.svg';
import Modal from '@twicapp/ui/components/Modal';

import { SettingsRoutes } from 'utils/routeGenerator';

const StyledModal = styled(Modal)`
  & > div {
    width: auto;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WelcomeText = styled(Headline1)`
  margin-bottom: 18px;
  font-size: 30px;
`;
const SubHeader = styled(Subtitle1)`
  margin-bottom: 16px;
`;
const AwesomeButton = styled(Button)`
  width: 100%;
  height: 42px;
  margin-top: 24px;
`;

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledLinkIcon = styled(SvgIcon).attrs({
  icon: LinkSvg,
  stroke: 'interactive',
})`
  margin-right: 10px;
  margin-top: 3px;
`;

const ExternalLink = styled.a.attrs({
  target: '_blank',
})`
  display: flex;
  text-decoration: none;
  margin-bottom: 10px;
  font-size: 16px;
`;

const StyledDivider = styled(Divider)`
  margin: 0px 0px 10px 0px;
`;

const InterstitialTaskModal = ({
  employee,
  company,
  onClose,
  visible,
}: $TSFixMe) => {
  const {
    features: { direct_deposit, marketplace },
  } = company;

  const fireAmplitude = (task_name: $TSFixMe) => {
    Amplitude.logEvent(Amplitude.EVENTS.OnboardingInterstitialTaskPressed, {
      task: task_name,
    });
  };

  const renderDirectDepositLink = () => (
    <ExternalLink
      href={`https://client.twic.ai${SettingsRoutes.payment()}`}
      onClick={() => fireAmplitude('Set up direct deposit')}
    >
      <StyledLinkIcon />
      Set up direct deposit reimbursement
    </ExternalLink>
  );

  const renderTwicStoreLink = () => (
    <ExternalLink
      href={`https://client.twic.ai${SettingsRoutes.payment()}`}
      onClick={() => fireAmplitude('Adding backup payment method')}
    >
      <StyledLinkIcon />
      Adding a backup payment method
    </ExternalLink>
  );

  const renderProgramPolicyLink = () => (
    <ExternalLink
      href={company.settings.reimbursement_policy_link}
      onClick={() => fireAmplitude('Program policy')}
    >
      <StyledLinkIcon />
      {`${company.company_info.common_name}'s program policy`}
    </ExternalLink>
  );

  const renderTwicInfoLink = () => (
    <ExternalLink
      href={'https://support.twic.ai/'}
      onClick={() => fireAmplitude('How to use Twic')}
    >
      <StyledLinkIcon />
      How to use Twic
    </ExternalLink>
  );

  return (
    <StyledModal open={visible} onClose={onClose} hide_close_button>
      <ModalContent>
        <Lottie
          animationData={BALLON_ANIMATION}
          play
          style={{
            width: '250px',
            height: '250px',
            objectFit: 'contain',
          }}
        />
        <WelcomeText>{`Welcome aboard, ${
          employee.preferred_first_name || employee.first_name
        }`}</WelcomeText>
        <SubHeader>Quickstart Guide</SubHeader>
        <StyledDivider />
        <LinksContainer>
          {direct_deposit && renderDirectDepositLink()}
          {marketplace && renderTwicStoreLink()}
          {renderProgramPolicyLink()}
          {renderTwicInfoLink()}
        </LinksContainer>
        <StyledDivider />
        <AwesomeButton variant="interactive" onClick={() => onClose()}>
          Awesome
        </AwesomeButton>
      </ModalContent>
    </StyledModal>
  );
};

export default InterstitialTaskModal;
